import React, { useState, useEffect } from "react";
import { requestChartAIAnalysis } from "../utils/aiAnalyzerEngine";
import "./AIAnalyzerModal.css";

function renderFormattedText(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} style={{ fontWeight: 700, color: "#0f172a" }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

const AIAnalyzerModal = ({ isOpen, onClose, chartPayload }) => {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen || !chartPayload) return;

    let isSubscribed = true;
    setLoading(true);
    setAnalysis(null);

    requestChartAIAnalysis(chartPayload)
      .then((data) => {
        if (isSubscribed) {
          setAnalysis(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("AI Analysis error:", err);
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [isOpen, chartPayload]);

  if (!isOpen) return null;

  const handleCopy = () => {
    if (!analysis) return;
    const cleanText = (str) => String(str || "").replace(/\*\*/g, "");
    const text = `
=== AI GRAPH ANALYSIS: ${chartPayload?.chart_title || "Chart"} ===
Overall Trend: ${cleanText(analysis.overall_trend)}

Possible Causes & Insights:
${(analysis.possible_causes || []).map((c) => `• ${cleanText(c)}`).join("\n")}

Recommended Actions & Preventive Measures:
${(analysis.recommended_actions || []).map((a) => `• ${cleanText(a)}`).join("\n")}

Concise Summary:
${cleanText(analysis.concise_summary)}
`.trim();

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stats = analysis?.stats || {};
  const unit = chartPayload?.unit ? ` ${chartPayload.unit}` : "";

  const getTrendBadge = (trendStr) => {
    const t = (trendStr || "Stable").toLowerCase();
    if (t.includes("increas")) return { icon: "📈", label: "Increasing", color: "#10b981", bg: "#ecfdf5" };
    if (t.includes("decreas")) return { icon: "📉", label: "Decreasing", color: "#ef4444", bg: "#fef2f2" };
    if (t.includes("fluctuat")) return { icon: "🌊", label: "Fluctuating", color: "#f59e0b", bg: "#fffbeb" };
    return { icon: "➡️", label: "Stable", color: "#3b82f6", bg: "#eff6ff" };
  };

  const trendBadge = getTrendBadge(stats.trend_direction);

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-modal-header">
          <div className="ai-modal-title-group">
            <div className="ai-sparkle-badge">
              <span>AI Analyzer</span>
            </div>
            <h3 className="ai-modal-chart-title">{chartPayload?.chart_title || "Chart Analysis"}</h3>
          </div>
          <button className="ai-modal-close-btn" onClick={onClose} aria-label="Close modal">
            ✕
          </button>
        </div>

        {/* Content Area */}
        <div className="ai-modal-body">
          {loading ? (
            <div className="ai-loading-state">
              <div className="ai-spinner-ring">
                <div className="ai-spinner-core" />
              </div>
              <div className="ai-loading-text">Analyzing graph trends & domain metrics...</div>
              <div className="ai-loading-subtext">Evaluating timestamp range, load profiles, and operational parameters</div>
            </div>
          ) : analysis ? (
            <>
              {/* Stats Bar */}
              <div className="ai-stats-bar">
                <div className="ai-stat-card">
                  <span className="ai-stat-label">Minimum</span>
                  <span className="ai-stat-value">{stats.min != null ? `${stats.min}${unit}` : "–"}</span>
                </div>
                <div className="ai-stat-card">
                  <span className="ai-stat-label">Maximum</span>
                  <span className="ai-stat-value highlight">{stats.max != null ? `${stats.max}${unit}` : "–"}</span>
                </div>
                <div className="ai-stat-card">
                  <span className="ai-stat-label">Average</span>
                  <span className="ai-stat-value">{stats.avg != null ? `${stats.avg}${unit}` : "–"}</span>
                </div>
                <div
                  className="ai-stat-card trend-card"
                  style={{ backgroundColor: trendBadge.bg, borderColor: trendBadge.color }}
                >
                  <span className="ai-stat-label" style={{ color: trendBadge.color }}>Overall Trend</span>
                  <span className="ai-stat-value" style={{ color: trendBadge.color }}>
                    {trendBadge.icon} {trendBadge.label}
                  </span>
                </div>
              </div>

              {/* Section 1: Executive Summary */}
              <div className="ai-section-card summary-card">
                <div className="ai-section-header">
                  <span className="ai-section-icon">📊</span>
                  <h4>Concise Summary</h4>
                </div>
                <p className="ai-section-text">{renderFormattedText(analysis.concise_summary)}</p>
              </div>

              {/* Section 2: Overall Trend */}
              <div className="ai-section-card trend-section">
                <div className="ai-section-header">
                  <span className="ai-section-icon">📈</span>
                  <h4>Overall Trend Analysis</h4>
                </div>
                <p className="ai-section-text">{renderFormattedText(analysis.overall_trend)}</p>
              </div>

              {/* Section 3: Possible Causes */}
              <div className="ai-section-card causes-card">
                <div className="ai-section-header">
                  <span className="ai-section-icon">💡</span>
                  <h4>Possible Causes & Insights</h4>
                </div>
                <ul className="ai-list">
                  {Array.isArray(analysis.possible_causes) && analysis.possible_causes.length > 0 ? (
                    analysis.possible_causes.map((cause, idx) => (
                      <li key={idx} className="ai-list-item">
                        <span className="ai-bullet-icon">🔍</span>
                        <span>{renderFormattedText(cause)}</span>
                      </li>
                    ))
                  ) : (
                    <li className="ai-list-item"><span>Standard operational performance observed.</span></li>
                  )}
                </ul>
              </div>

              {/* Section 4: Recommended Actions */}
              <div className="ai-section-card actions-card">
                <div className="ai-section-header">
                  <span className="ai-section-icon">🛠️</span>
                  <h4>Recommended Actions & Preventive Measures</h4>
                </div>
                <ul className="ai-list">
                  {Array.isArray(analysis.recommended_actions) && analysis.recommended_actions.length > 0 ? (
                    analysis.recommended_actions.map((action, idx) => (
                      <li key={idx} className="ai-list-item action-item">
                        <span className="ai-bullet-icon action-bullet">✓</span>
                        <span>{renderFormattedText(action)}</span>
                      </li>
                    ))
                  ) : (
                    <li className="ai-list-item"><span>No immediate corrective action required.</span></li>
                  )}
                </ul>
              </div>
            </>
          ) : (
            <div className="ai-error-state">
              <span>Failed to generate graph analysis. Please try again.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ai-modal-footer">
          <button
            className={`ai-copy-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
            disabled={loading || !analysis}
          >
            {copied ? "✓ Copied to Clipboard" : "📋 Copy Analysis Report"}
          </button>
          <button className="ai-close-footer-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAnalyzerModal;
